import { useMachine } from "@xstate/react";
import React, { FunctionComponent, useEffect, useMemo, useRef } from "react";
import { Node } from "@iventis/domain-model/model/node";
import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
import { ScheduleStatus } from "@iventis/domain-model/model/scheduleStatus";
import { Schedule } from "@iventis/domain-model/model/schedule";
import { Content } from "@iventis/translations/content/typed-content";
import { EMPTY_GUID, xstateDevTools } from "@iventis/utilities";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FormWizardTemplate, Stages, LoadingComponent, DraftIcon, PublishedIcon, SelectItemsComponent } from "@iventis/components";
import { hasPermissionsChanged } from "@iventis/permissions/src/permission-utilitites";
import { PermissionsEditActorComponent } from "@iventis/permissions/src/permissions-edit-wrappers";
import { TextField } from "@mui/material";
import { State } from "xstate";
import { Body1, Body2, Header5, Subtitle } from "@iventis/styles/src/components/texts";
import { styled } from "@iventis/styles";
import { createPiblingNode, COPY_MACHINE_ID } from "./copy-schedule-versions.machine";
import { CreateScheduleContext, createScheduleMachine, CreateScheduleEvents } from "./create-schedule.machine";
import { CopyScheduleVersionComponent } from "./copy-schedule-versions";

export const CreateScheduleFormWizard: FunctionComponent<{
    close: () => void;
    piblings: Node[];
    schedule: Schedule;
    onFinish: (schedule: ScheduleVersion, copyCount: number) => void;
    onWaitingForCopy: (schedule: ScheduleVersion, pendingSchedules: { name: string }[]) => void;
}> = ({ close, piblings, schedule, onFinish, onWaitingForCopy }) => {
    const translate = useIventisTranslate();
    const title = translate(Content.schedule.create_schedule_form.create_new_schedule);
    // No dependencies as this is only the initial schedule version
    const initialScheduleObject: ScheduleVersion = useMemo(
        () => ({
            name: "",
            status: ScheduleStatus.Draft,
            dataFieldValues: [],
            type: schedule.config.scheduleVersionTypes.find((s) => s.allowedParentType === EMPTY_GUID),
            id: EMPTY_GUID,
            parentId: null,
            scheduleId: schedule.id,
            publishedAt: null,
        }),
        []
    );

    const getScheduleVersionCopySelection = (context: CreateScheduleContext) =>
        context.copyRef.getSnapshot().context.scheduleVersionIdsToCopy.filter(({ parentId }) => parentId !== null /* Do not include top level schedule versions */);

    // Since this is referenced inside a useref, we have to update a reference to it so on prop changes, the function used is up to date
    const latestOnWaitingForCopy = useRef<typeof onWaitingForCopy>();
    useEffect(() => {
        latestOnWaitingForCopy.current = onWaitingForCopy;
    }, [onWaitingForCopy]);

    const { current: scheduleMachine } = useRef(
        createScheduleMachine((context) =>
            latestOnWaitingForCopy.current(
                context.schedule,
                getScheduleVersionCopySelection(context).map(({ name }) => ({ name }))
            )
        )
    );

    const [state, send] = useMachine(scheduleMachine, {
        devTools: xstateDevTools,
        context: {
            schedule: initialScheduleObject,
            existingScheduleVersions: piblings.map((p) => createPiblingNode(p, false)),
        },
    });

    const { permissions } = state.context;
    const { latestPermissions, originalPermissions } = permissions;

    useEffect(() => {
        if (state.matches("finished")) {
            onFinish(state.context.schedule, getScheduleVersionCopySelection(state.context).length);
            close();
        }
    }, [state.value]);

    const Stages: Stages = [
        {
            primaryButtonText: translate(Content.common.buttons.create),
            primaryButtonCallback: () => send("CREATE"),
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: close }],
            isValid: state.matches("createStep.idle.valid"),
            Component: <CreateScheduleCreateStepComponent state={state} send={send} />,
        },
        {
            primaryButtonText: translate(Content.common.buttons.saveAndFinish),
            primaryButtonCallback: () =>
                hasPermissionsChanged(originalPermissions || [], latestPermissions || []) ? send([{ type: "SAVE" }, { type: "CLOSE" }]) : send({ type: "CLOSE" }),

            secondaryButtons: [{ buttonText: translate(Content.common.buttons.close), onButtonPressed: close }],
            isValid: state.context.isPermissionsIdle,
            Component: state.children.permissions != null && ( // We must have invoked the permissions machine in order to render the following component
                <>
                    <PermissionsEditActorComponent parentState={state} />
                </>
            ),
        },
    ];

    const currentStage = state.matches("createStep") ? 0 : state.matches("permissionsStep") ? 1 : null;

    return (
        <>
            {currentStage != null && (
                <FormWizardTemplate title={title} stages={Stages} currentStage={currentStage}>
                    {Stages[currentStage]?.Component}
                </FormWizardTemplate>
            )}
        </>
    );
};

const CreateScheduleCreateStepComponent: FunctionComponent<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    state: State<CreateScheduleContext, CreateScheduleEvents, any, any, any>;
    send: (params: CreateScheduleEvents | CreateScheduleEvents[]) => void;
}> = ({ state, send }) => {
    const translate = useIventisTranslate();
    return (
        <>
            {state.matches("createStep.loading") || state.matches("createStep.creating") ? (
                <LoadingComponent />
            ) : (
                <>
                    <Header5 className="input-label space-within-content">{translate(Content.schedule.create_schedule_form.name_field_label)}</Header5>
                    <TextField
                        key="name-field"
                        required
                        className="input-box space-between-content"
                        variant="outlined"
                        value={state.context.schedule.name}
                        onChange={(event) => send({ type: "UPDATE_NAME", payload: event.target.value })}
                    />
                    {state.context.validationError?.name ? <p>{state.context.validationError.name}</p> : null}
                    <Header5 className="input-label space-within-content">{translate(Content.schedule.create_schedule_form.status_field_label)}</Header5>
                    <div className="space-between-content" style={{ height: "fit-content" }}>
                        <StyledStatusSelector
                            options={[
                                { id: "Draft", name: "Draft", component: DraftSelector },
                                { id: "Published", name: "Published", component: PublishedSelector },
                            ]}
                            value={state.context.schedule.status}
                            verticalPadding={12}
                            changeValue={(output: string) => {
                                send({ type: "UPDATE_STATUS", payload: ScheduleStatus[output] });
                            }}
                            className="status"
                        />
                    </div>
                    <Body1 className="input-label space-within-content">{translate(Content.schedule.create_schedule_form.copy_versions_label)}</Body1>
                    {state.children[COPY_MACHINE_ID] && <CopyScheduleVersionComponent parentState={state} childType={state.context.schedule.type.name} />}
                </>
            )}
        </>
    );
};

export const DraftSelector = () => {
    const translate = useIventisTranslate();
    return (
        <StyledStatusSelectorItem>
            <DraftIcon />
            <Subtitle>{translate(Content.schedule.create_schedule_form.status_field_options.draft_label)}</Subtitle>
            <Body2>{translate(Content.schedule.create_schedule_form.status_field_options.draft_description)}</Body2>
        </StyledStatusSelectorItem>
    );
};

export const PublishedSelector = () => {
    const translate = useIventisTranslate();
    return (
        <StyledStatusSelectorItem>
            <PublishedIcon />
            <Subtitle>{translate(Content.schedule.create_schedule_form.status_field_options.published_label)}</Subtitle>
            <Body2>{translate(Content.schedule.create_schedule_form.status_field_options.published_description)}</Body2>
        </StyledStatusSelectorItem>
    );
};

export const StyledStatusSelector = styled(SelectItemsComponent)`
    .status {
        .MuiInputBase-input {
            height: fit-content;
        }
    }
`;

const StyledStatusSelectorItem = styled.div`
    display: grid;
    grid-template-columns: 140px auto;
    grid-template-rows: 1fr 1fr;
    svg {
        grid-column: 1 / 2;
        grid-row: 1 / 3;
    }
    h6 {
        grid-row: 1 / 2;
        grid-column: 2 / 3;
    }
    p {
        grid-column: 2 / 3;
        grid-row: 2 / 3;
    }
`;
